import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Layers, Trash2, Eye, EyeOff, FileImage, FileJson, GripVertical, ChevronUp, ChevronDown, MapPinned, Download, FileSpreadsheet } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);


// Built-in sample khasra parcels (84 demo polygons) — site-wide on/off like any other layer.
function DemoParcelsToggle() {
  const { settings, refreshSettings } = useAuth();
  const [busy, setBusy] = useState(false);
  const on = settings?.show_demo_parcels !== false;
  const toggle = async (v) => {
    setBusy(true);
    try { await api.put("/admin/settings", { show_demo_parcels: v }); await refreshSettings(); toast.success(v ? "Demo parcels shown on map" : "Demo parcels hidden from map"); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="demo-parcels-row" className={`p-5 border-b flex items-center gap-4 ${on ? "" : "opacity-70"}`}>
      <span className="w-10 h-10 rounded-xl grid place-items-center bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"><MapPinned size={18} /></span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Demo parcels <Badge variant="outline" className="rounded-full text-[10px] ml-1">built-in</Badge></p>
        <p className="text-xs text-muted-foreground">84 sample khasra polygons with fictitious owners (legend "Parcel land type"). Not a file — turn off once your real data is uploaded.</p>
      </div>
      <Switch data-testid="demo-parcels-toggle" checked={on} disabled={busy} onCheckedChange={toggle} aria-label="Show demo parcels" />
    </div>
  );
}

const LOC_FIELDS = [["district", "District"], ["tehsil", "Tehsil"], ["ri", "R.I."], ["village", "Village"]];

// File → location mapping (ported from Bolt "Village File Mappings"): drives the cascading dropdowns on the map.
function LocationMapping({ layer, layers, onSave }) {
  const [edit, setEdit] = useState(false);
  const [loc, setLoc] = useState(() => Object.fromEntries(LOC_FIELDS.map(([k]) => [k, layer[k] || ""])));
  const mapped = LOC_FIELDS.map(([k]) => layer[k]).filter(Boolean);
  if (!edit) {
    return (
      <button type="button" data-testid="admin-layer-location-button" onClick={() => setEdit(true)} className="mt-0.5 text-[11px] flex items-center gap-1 text-emerald-800 dark:text-emerald-300 hover:underline">
        <MapPinned size={11} /> {mapped.length ? mapped.join(" › ") : "Map to district / tehsil / village"}
      </button>
    );
  }
  return (
    <form data-testid="admin-layer-location-form" onSubmit={(e) => { e.preventDefault(); onSave(loc); setEdit(false); }} className="mt-1 flex flex-wrap gap-1 items-center">
      {LOC_FIELDS.map(([k, label]) => (
        <span key={k}>
          <input list={`loc-${k}`} data-testid={`admin-layer-location-${k}`} value={loc[k]} onChange={(e) => setLoc({ ...loc, [k]: e.target.value })} placeholder={label} className="h-7 w-24 rounded-lg border bg-card px-2 text-[11px]" />
          <datalist id={`loc-${k}`}>{[...new Set(layers.map((x) => x[k]).filter(Boolean))].map((v) => <option key={v} value={v} />)}</datalist>
        </span>
      ))}
      <button type="submit" data-testid="admin-layer-location-save" className="h-7 px-2 rounded-lg bg-emerald-900 text-white dark:bg-emerald-500 dark:text-slate-950 text-[11px]">Save</button>
      <button type="button" onClick={() => setEdit(false)} className="h-7 px-2 rounded-lg border text-[11px]">Cancel</button>
    </form>
  );
}

export function UploadCard({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#10B981");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const pick = (f) => { if (f) { setFile(f); if (!name) setName(f.name.replace(/\.[^.]+$/, "")); } };
  const downloadTemplate = async (fmt) => {
    try {
      const res = await api.get(`/admin/layers/template/${fmt}`, { responseType: "blob" });
      const fname = /filename="?([^"]+)"?/.exec(res.headers["content-disposition"] || "")?.[1] || `template.${fmt}`;
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = fname; a.click(); URL.revokeObjectURL(a.href);
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("file", file); fd.append("name", name); fd.append("color", color);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/layers", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.success(`Layer "${data.name}" added${data.kind === "vector" ? ` · ${data.feature_count} features` : " · raster overlay"}`);
      setFile(null); setName(""); if (inputRef.current) inputRef.current.value = "";
      onUploaded(data);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="layer-upload-card" className="rounded-2xl border bg-card p-7 space-y-5">
      <p className="data-label flex items-center gap-2"><UploadCloud size={14} /> Upload map layer</p>
      <div data-testid="layer-dropzone" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }} onClick={() => inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${drag ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-slate-300 dark:border-slate-700 hover:border-emerald-400"}`}>
        <Layers className="mx-auto text-emerald-700 dark:text-emerald-400" />
        <p className="mt-3 font-semibold text-sm">{file ? file.name : "Drop a file or click to browse"}</p>
        <p className="text-xs text-muted-foreground mt-1">{file ? fmtSize(file.size) : "GeoTIFF (.tif) · KML / KMZ · Shapefile (.zip with .shp/.shx/.dbf/.prj) · GeoJSON · up to 100 MB"}</p>
        <input ref={inputRef} data-testid="layer-file-input" type="file" className="hidden" accept=".tif,.tiff,.kml,.kmz,.zip,.geojson,.json" onChange={(e) => pick(e.target.files?.[0])} />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">Templates with standard fields (khasra, owner, village, district, area_ha, land_type, survey_year):</span>
        {[["kml", "KML"], ["shapefile", "Shapefile"], ["geojson", "GeoJSON"], ["csv", "CSV"]].map(([fmt, label]) => (
          <button key={fmt} type="button" data-testid={`template-download-${fmt}`} onClick={() => downloadTemplate(fmt)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border hover:bg-muted font-mono"><Download size={11} /> {label}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3">
        <div className="space-y-2"><Label htmlFor="layer-name">Layer name</Label><Input id="layer-name" data-testid="layer-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Raipur village boundaries" className="h-11 rounded-xl" /></div>
        <div className="space-y-2"><Label htmlFor="layer-color">Colour</Label><input id="layer-color" data-testid="layer-color-input" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-11 w-16 rounded-xl border bg-transparent p-1 cursor-pointer" /></div>
      </div>
      <Button data-testid="layer-upload-button" type="submit" disabled={busy || !file} className="rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950">
        {busy ? "Processing…" : "Upload & publish"}
      </Button>
      <p className="text-xs text-muted-foreground">Vectors are reprojected to WGS84 and stored as GeoJSON (auto-simplified above 12 MB). GeoTIFFs are rendered to a georeferenced PNG overlay (max 2048 px).</p>
    </form>
  );
}

export function MappingCsv({ onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/layers/mapping-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Mapped ${data.updated} of ${data.rows} rows`, { description: data.missing_count ? `Not found: ${data.missing.join(", ")}` : undefined, duration: 8000 });
      onDone();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const download = async () => {
    const { data } = await api.get("/admin/layers/mapping-csv", { responseType: "blob" });
    const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = "cgland-layer-mapping.csv"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" data-testid="mapping-csv-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
      <Button data-testid="mapping-csv-template-button" variant="outline" size="sm" className="rounded-full h-9" onClick={download} title="Download current mapping as CSV template"><Download size={14} className="mr-1" /> CSV template</Button>
      <Button data-testid="mapping-csv-upload-button" size="sm" className="rounded-full h-9 bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950" disabled={busy} onClick={() => ref.current?.click()}><FileSpreadsheet size={14} className="mr-1" /> {busy ? "Importing…" : "Import mapping CSV"}</Button>
    </div>
  );
}

export function AdminLayers() {
  const [layers, setLayers] = useState([]);
  const load = () => api.get("/admin/layers").then(({ data }) => setLayers(data)).catch((err) => toast.error(errorMessage(err)));
  useEffect(() => { load(); }, []);

  const patch = async (l, body) => {
    try { const { data } = await api.patch(`/admin/layers/${l.id}`, body); setLayers((ls) => ls.map((x) => (x.id === l.id ? data : x))); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Remove layer "${l.name}" from the map?`)) return;
    try { await api.delete(`/admin/layers/${l.id}`); setLayers((ls) => ls.filter((x) => x.id !== l.id)); toast.success("Layer removed"); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const commitOrder = async (list) => {
    setLayers(list);
    try { const { data } = await api.put("/admin/layers/reorder", { ids: list.map((l) => l.id) }); setLayers(data); toast.success("Layer order saved"); }
    catch (err) { toast.error(errorMessage(err)); load(); }
  };
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return setDragId(null);
    const list = [...layers];
    const from = list.findIndex((l) => l.id === dragId);
    const to = list.findIndex((l) => l.id === targetId);
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setDragId(null); setOverId(null);
    commitOrder(list);
  };
  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= layers.length) return;
    const list = [...layers];
    [list[idx], list[to]] = [list[to], list[idx]];
    commitOrder(list);
  };

  return (
    <div data-testid="admin-layers" className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2"><UploadCard onUploaded={(l) => setLayers((ls) => [l, ...ls])} /></div>
      <div className="lg:col-span-3 rounded-2xl border bg-card overflow-hidden">
        <div className="p-5 border-b flex flex-col sm:flex-row sm:items-center gap-3"><div className="flex-1"><p className="font-semibold">Published layers</p><p className="text-xs text-muted-foreground">{layers.length} layers · drag to reorder — top of this list draws on top of the map · CSV columns: district, tehsil, ri, village, file</p></div><MappingCsv onDone={load} /></div>
        <DemoParcelsToggle />
        {layers.length === 0 && <p data-testid="admin-layers-empty" className="p-8 text-sm text-muted-foreground text-center">No layers uploaded yet. Add a GeoTIFF, KML or shapefile to get started.</p>}
        <ul className="divide-y">
          {layers.map((l, idx) => (
            <li key={l.id} data-testid={`admin-layer-row-${l.id}`} draggable onDragStart={() => setDragId(l.id)} onDragOver={(e) => { e.preventDefault(); setOverId(l.id); }} onDragLeave={() => setOverId(null)} onDrop={() => onDrop(l.id)} onDragEnd={() => { setDragId(null); setOverId(null); }}
              className={`p-5 flex flex-col lg:flex-row lg:items-center gap-4 transition-colors ${overId === l.id && dragId !== l.id ? "bg-emerald-50 dark:bg-emerald-950/30" : ""} ${dragId === l.id ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-1 shrink-0">
                <span className="cursor-grab active:cursor-grabbing text-muted-foreground" title="Drag to reorder" data-testid="admin-layer-drag-handle"><GripVertical size={18} /></span>
                <div className="flex flex-col">
                  <button type="button" data-testid="admin-layer-move-up" aria-label="Move up" disabled={idx === 0} onClick={() => move(idx, -1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button type="button" data-testid="admin-layer-move-down" aria-label="Move down" disabled={idx === layers.length - 1} onClick={() => move(idx, 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronDown size={14} /></button>
                </div>
              </div>
              <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${l.color}22`, color: l.color }}>{l.kind === "raster" ? <FileImage size={18} /> : <FileJson size={18} />}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate" data-testid="admin-layer-name">{l.name}</p>
                <LocationMapping layer={l} layers={layers} onSave={(loc) => patch(l, loc)} />
                <div className="text-xs text-muted-foreground truncate">
                  <Badge variant="outline" className="mr-2 font-mono uppercase text-[10px]">{l.source_format}</Badge>
                  {l.kind === "vector" ? `${l.feature_count} features · ${(l.geometry_types || []).join(", ")}` : `${l.width}×${l.height} px · ${l.bands} band${l.bands > 1 ? "s" : ""}`} · {fmtSize(l.size_bytes)} · {formatDate(l.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-muted-foreground" title="Default opacity">
                  <input type="range" min={0} max={100} step={5} data-testid="admin-layer-opacity-input" defaultValue={Math.round((l.opacity ?? 0.8) * 100)}
                    onMouseUp={(e) => patch(l, { opacity: Number(e.target.value) / 100 })} onTouchEnd={(e) => patch(l, { opacity: Number(e.target.value) / 100 })}
                    className="w-20 h-1.5 accent-emerald-700 cursor-pointer" aria-label="Default opacity" />
                  <span className="font-mono w-8">{Math.round((l.opacity ?? 0.8) * 100)}%</span>
                </label>
                <input type="color" aria-label="Layer colour" data-testid="admin-layer-color-input" value={l.color} onChange={(e) => patch(l, { color: e.target.value })} className="h-8 w-8 rounded-lg border bg-transparent p-0.5 cursor-pointer" />
                <label className="flex items-center gap-2 text-xs text-muted-foreground">{l.visible ? <Eye size={14} /> : <EyeOff size={14} />}<Switch data-testid="admin-layer-visible-switch" checked={l.visible} onCheckedChange={(v) => patch(l, { visible: v })} className="data-[state=checked]:bg-emerald-600" /></label>
                <Button data-testid="admin-layer-delete-button" variant="ghost" size="icon" className="rounded-full text-red-600 hover:bg-red-50" onClick={() => remove(l)}><Trash2 size={16} /></Button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
